/**
 * ContractListPage
 * 전자계약 목록 페이지
 */

import React, { useState, useEffect } from 'react';
import { ContractCreateModal } from './ContractCreateModal';
import { ContractDetailModal } from './ContractDetailModal';

interface Contract {
  id: string;
  contract_number: string;
  contract_type: string;
  title: string;
  status: string;
  signer_name: string;
  signer_phone: string;
  signer_email?: string;
  organization_name?: string;
  expires_at?: string;
  signed_at?: string;
  completed_at?: string;
  created_at: string;
}

interface ContractListResponse {
  success: boolean;
  items: Contract[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: '초안', color: 'bg-gray-100 text-gray-800' },
  sent: { label: '발송됨', color: 'bg-blue-100 text-blue-800' },
  verified: { label: '인증완료', color: 'bg-yellow-100 text-yellow-800' },
  signed: { label: '서명완료', color: 'bg-green-100 text-green-800' },
  completed: { label: '완료', color: 'bg-emerald-100 text-emerald-800' },
  expired: { label: '만료', color: 'bg-red-100 text-red-800' },
  cancelled: { label: '취소', color: 'bg-gray-100 text-gray-600' },
};

const TYPE_LABELS: Record<string, string> = {
  subscription: '정기구독',
  onetime: '일회성',
};

export const ContractListPage: React.FC = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 필터 상태
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [search, setSearch] = useState('');

  // 페이지네이션
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // 모달 상태
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);

  // 통계
  const [stats, setStats] = useState<Record<string, number>>({});

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
      });
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('contract_type', typeFilter);
      if (search) params.append('search', search);

      const response = await fetch(`/api/admin/contracts?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });

      if (!response.ok) throw new Error('계약 목록 조회 실패');

      const data: ContractListResponse = await response.json();
      setContracts(data.items || []);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류 발생');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/admin/contracts/statistics', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.statistics?.by_status || {});
      }
    } catch (err) {
      console.error('통계 조회 실패:', err);
    }
  };

  useEffect(() => {
    fetchContracts();
    fetchStats();
  }, [page, statusFilter, typeFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchContracts();
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    fetchContracts();
    fetchStats();
  };

  const handleDetailClose = () => {
    setSelectedContract(null);
    fetchContracts();
    fetchStats();
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">전자계약 관리</h1>
          <p className="text-sm text-gray-600 mt-1">계약서 생성, 발송 및 서명 관리</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
        >
          <span>+</span>
          <span>새 계약 생성</span>
        </button>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        {Object.entries(STATUS_LABELS).map(([key, { label, color }]) => (
          <div
            key={key}
            onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
            className={`p-4 rounded-lg border cursor-pointer transition-all ${
              statusFilter === key ? 'ring-2 ring-indigo-500' : ''
            } ${color.replace('text-', 'hover:border-')}`}
          >
            <div className="text-2xl font-bold">{stats[key] || 0}</div>
            <div className="text-sm text-gray-600">{label}</div>
          </div>
        ))}
      </div>

      {/* 필터 및 검색 */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-4 flex-wrap">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border rounded-lg"
          >
            <option value="">전체 상태</option>
            {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>

          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border rounded-lg"
          >
            <option value="">전체 유형</option>
            {Object.entries(TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>

          <div className="flex-1 flex gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="계약번호, 제목, 서명자 검색"
              className="flex-1 px-3 py-2 border rounded-lg"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              검색
            </button>
          </div>
        </form>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* 계약 목록 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">로딩 중...</div>
        ) : contracts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            계약이 없습니다.
            <button
              onClick={() => setShowCreateModal(true)}
              className="block mx-auto mt-4 text-indigo-600 hover:underline"
            >
              새 계약 생성하기
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                  계약번호
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                  제목
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                  서명자
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                  유형
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                  상태
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                  생성일
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                  만료일
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {contracts.map((contract) => {
                const statusInfo = STATUS_LABELS[contract.status] || {
                  label: contract.status,
                  color: 'bg-gray-100 text-gray-800',
                };

                return (
                  <tr
                    key={contract.id}
                    onClick={() => setSelectedContract(contract)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 text-sm font-mono">
                      {contract.contract_number}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">
                      {contract.title}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div>{contract.signer_name}</div>
                      <div className="text-xs text-gray-500">
                        {contract.signer_phone}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {TYPE_LABELS[contract.contract_type] || contract.contract_type}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${statusInfo.color}`}
                      >
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(contract.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(contract.expires_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
            <span className="text-sm text-gray-600">
              총 {total}건 중 {(page - 1) * 20 + 1}-{Math.min(page * 20, total)}건
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                이전
              </button>
              <span className="px-3 py-1">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 모달 */}
      {showCreateModal && (
        <ContractCreateModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {selectedContract && (
        <ContractDetailModal
          contractId={selectedContract.id}
          onClose={handleDetailClose}
        />
      )}
    </div>
  );
};

export default ContractListPage;
